"use client";

import { useEffect, useMemo, useRef, useState, type HTMLAttributes } from "react";
import Lightbox, { type ZoomRef } from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import { ChevronLeft, ChevronRight, Minus, Plus, RotateCcw, X } from "lucide-react";

import { cn } from "@/utils/cn";

export type SharedImageLightboxSlide = {
  id: string;
  src: string;
  alt: string;
  title?: string | null;
  description?: string | null;
};

export type SharedImageLightboxCopy = {
  close: string;
  zoomIn: string;
  zoomOut: string;
  resetZoom: string;
  previous?: string;
  next?: string;
};

const DEFAULT_COPY: SharedImageLightboxCopy = {
  close: "Close",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  resetZoom: "Reset zoom",
  previous: "Previous image",
  next: "Next image",
};

type SharedImageLightboxProps = {
  open: boolean;
  slides: SharedImageLightboxSlide[];
  index: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
  copy?: Partial<SharedImageLightboxCopy>;
  testId?: string;
  className?: string;
};

function clampIndex(index: number, total: number): number {
  if (total <= 0) return 0;
  if (index < 0) return 0;
  if (index >= total) return total - 1;
  return index;
}

export function SharedImageLightbox({
  open,
  slides,
  index,
  onClose,
  onIndexChange,
  copy,
  testId = "media-viewer",
  className,
}: SharedImageLightboxProps) {
  const labels = useMemo(() => ({ ...DEFAULT_COPY, ...copy }), [copy]);
  const zoomRef = useRef<ZoomRef>(null);
  const [displayIndex, setDisplayIndex] = useState(clampIndex(index, slides.length));
  const [zoomValue, setZoomValue] = useState(1);

  useEffect(() => {
    setDisplayIndex(clampIndex(index, slides.length));
  }, [index, slides.length]);

  useEffect(() => {
    if (!open) {
      setZoomValue(1);
    }
  }, [open]);

  if (slides.length === 0) {
    return null;
  }

  const safeIndex = clampIndex(index, slides.length);
  const currentSlide = slides[clampIndex(displayIndex, slides.length)] ?? slides[0];

  return (
    <Lightbox
      open={open}
      close={onClose}
      index={safeIndex}
      slides={slides.map((slide) => ({
        src: slide.src,
        alt: slide.alt,
      }))}
      className={cn("shared-image-lightbox", className)}
      plugins={[Zoom]}
      zoom={{
        ref: zoomRef,
        maxZoomPixelRatio: 4,
        zoomInMultiplier: 1.25,
        scrollToZoom: true,
        pinchZoomV4: true,
      }}
      carousel={{
        finite: slides.length <= 1,
        padding: 24,
        spacing: 16,
        imageFit: "contain",
        imageProps: {
          draggable: false,
        },
      }}
      controller={{
        closeOnBackdropClick: true,
        closeOnPullDown: true,
        closeOnPullUp: false,
      }}
      portal={{
        container: {
          "data-testid": testId,
        } as HTMLAttributes<HTMLDivElement>,
      }}
      labels={{
        Close: labels.close,
        Previous: labels.previous ?? DEFAULT_COPY.previous!,
        Next: labels.next ?? DEFAULT_COPY.next!,
        "Zoom in": labels.zoomIn,
        "Zoom out": labels.zoomOut,
      }}
      animation={{
        fade: 220,
        swipe: 320,
        navigation: 260,
        zoom: 180,
        easing: {
          fade: "ease",
          swipe: "cubic-bezier(0.22, 1, 0.36, 1)",
          navigation: "cubic-bezier(0.22, 1, 0.36, 1)",
        },
      }}
      toolbar={{ buttons: [] }}
      styles={{
        container: {
          "--yarl__portal_zindex": 140,
          "--yarl__container_background_color": "rgba(17, 24, 22, 0.94)",
          "--yarl__color_backdrop": "rgba(17, 24, 22, 0.94)",
          "--yarl__color_button": "rgba(247, 241, 230, 0.84)",
          "--yarl__color_button_active": "#fff8ee",
          "--yarl__color_button_disabled": "rgba(247, 241, 230, 0.34)",
          "--yarl__navigation_button_padding": "16px 14px",
        },
        button: {
          backgroundColor: "rgba(33, 44, 41, 0.78)",
          border: "1px solid rgba(216, 202, 178, 0.28)",
          borderRadius: 999,
          backdropFilter: "blur(18px)",
        },
        navigationPrev: {
          left: "18px",
        },
        navigationNext: {
          right: "18px",
        },
      }}
      render={{
        iconPrev: () => <ChevronLeft className="h-7 w-7" />,
        iconNext: () => <ChevronRight className="h-7 w-7" />,
        controls: () => (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] px-4 py-4 sm:px-6 sm:py-6">
            <div className="mx-auto flex max-w-6xl items-start justify-between gap-4">
              <div className="pointer-events-auto min-w-0 space-y-1 rounded-[28px] border border-[#d8cab2]/18 bg-[#111816]/72 px-4 py-3 text-[#f7f1e6] shadow-[0_24px_80px_rgba(0,0,0,0.22)] backdrop-blur-2xl">
                <p className="truncate text-sm font-semibold text-[#f7f1e6]/96">{currentSlide.title || currentSlide.alt}</p>
                {currentSlide.description ? (
                  <p className="max-w-2xl text-sm leading-6 text-[#d9d5cd]">{currentSlide.description}</p>
                ) : null}
              </div>

              <div className="pointer-events-auto flex shrink-0 items-center gap-2 rounded-full border border-[#d8cab2]/18 bg-[#111816]/72 p-2 shadow-[0_24px_80px_rgba(0,0,0,0.22)] backdrop-blur-2xl">
                <button
                  type="button"
                  onClick={() => zoomRef.current?.zoomOut()}
                  data-testid="media-viewer-zoom-out"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#d8cab2]/22 bg-[#18211f] text-[#f7f1e6] transition-colors hover:bg-[#1e2926]"
                  aria-label={labels.zoomOut}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => zoomRef.current?.changeZoom(1, true)}
                  data-testid="media-viewer-zoom-value"
                  className="inline-flex min-w-[84px] items-center justify-center rounded-full border border-[#d8cab2]/22 bg-[#18211f] px-3 py-3 text-xs font-semibold tracking-[0.08em] text-[#f7f1e6] transition-colors hover:bg-[#1e2926]"
                  aria-label={labels.resetZoom}
                >
                  {Math.round(zoomValue * 100)}%
                </button>
                <button
                  type="button"
                  onClick={() => zoomRef.current?.zoomIn()}
                  data-testid="media-viewer-zoom-in"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#d8cab2]/22 bg-[#18211f] text-[#f7f1e6] transition-colors hover:bg-[#1e2926]"
                  aria-label={labels.zoomIn}
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => zoomRef.current?.changeZoom(1, true)}
                  data-testid="media-viewer-zoom-reset"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#d8cab2]/22 bg-[#18211f] text-[#f7f1e6] transition-colors hover:bg-[#1e2926]"
                  aria-label={labels.resetZoom}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="media-viewer-close"
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#d8cab2]/24 bg-[#1f2c29] px-4 text-sm font-semibold text-[#f7f1e6] transition-colors hover:bg-[#263430]"
                >
                  <X className="h-4 w-4" />
                  {labels.close}
                </button>
              </div>
            </div>
          </div>
        ),
      }}
      on={{
        view: ({ index: nextIndex }) => {
          const resolvedIndex = clampIndex(nextIndex, slides.length);
          setDisplayIndex(resolvedIndex);
          onIndexChange?.(resolvedIndex);
        },
        zoom: ({ zoom }) => {
          setZoomValue(zoom);
        },
        entering: () => {
          setZoomValue(1);
        },
        exited: () => {
          setZoomValue(1);
        },
      }}
    />
  );
}
